import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../services/supabaseClient'
import { useAppSelector } from '../store/hooks'

export interface CommitmentsData {
  beh_start?: string
  beh_stop?: string
  beh_double?: string
  beh_start_ratings?: string[]
  beh_stop_ratings?: string[]
  beh_double_ratings?: string[]
  tool_weekly?: string
  tool_monthly?: string
  tool_quarterly?: string
  tool_weekly_log?: string[]
  tool_monthly_log?: string[]
  tool_quarterly_log?: string[]
  tool_weekly_ratings?: string[]
  tool_monthly_ratings?: string[]
  tool_quarterly_ratings?: string[]
}

const TOOL_ID = '_habits'

export function useCommitments() {
  const userId = useAppSelector((s) => s.auth.user?.id)
  const [data, setData] = useState<CommitmentsData | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!userId) { setIsLoading(false); return }

    async function load() {
      const { data: rows } = await supabase
        .from('responses')
        .select('data')
        .eq('user_id', userId!)
        .eq('tool_id', TOOL_ID)
        .maybeSingle()

      setData((rows?.data as CommitmentsData) ?? null)
      setIsLoading(false)
    }

    load()
  }, [userId])

  const save = useCallback(async (newData: CommitmentsData) => {
    if (!userId) return
    setData(newData)

    const { data: existing } = await supabase
      .from('responses')
      .select('id')
      .eq('user_id', userId)
      .eq('tool_id', TOOL_ID)
      .maybeSingle()

    if (existing) {
      await supabase
        .from('responses')
        .update({ data: newData })
        .eq('user_id', userId)
        .eq('tool_id', TOOL_ID)
    } else {
      await supabase
        .from('responses')
        .insert({ user_id: userId, tool_id: TOOL_ID, data: newData })
    }
  }, [userId])

  const addRating = useCallback(async (key: string, rating: string) => {
    const current = data ?? {}
    const ratingsKey = `${key}_ratings` as keyof CommitmentsData
    const ratings = [...((current[ratingsKey] as string[]) ?? []), rating]
    const updated = { ...current, [ratingsKey]: ratings }
    await save(updated)
  }, [data, save])

  const logToolCompletion = useCallback(async (freqKey: string) => {
    const current = data ?? {}
    const logKey = `tool_${freqKey}_log` as keyof CommitmentsData
    const log = [...((current[logKey] as string[]) ?? []), new Date().toISOString()]
    const updated = { ...current, [logKey]: log }
    await save(updated)
  }, [data, save])

  const isSetup = !!(data?.beh_start || data?.tool_weekly)

  return { data, isLoading, isSetup, save, addRating, logToolCompletion }
}
